import { DialogModal } from 'components/common';
import { ChatBanner, FilterPanelContent, SidePanel } from 'components/core';
import { LDFlagSet } from 'launchdarkly-js-client-sdk';
import { useContext, useState } from 'react';
import { FilterContext } from 'utils/context';
import { TFilterConfig } from 'utils/types';

import { Box, Button, CSS, Flex } from '@project44/manifest-ds';
import { useTranslation } from '@project44/translations-utils';

interface IFilterPanel {
  filters: (flags: LDFlagSet) => TFilterConfig;
  doSearch: () => void;
  onClose: () => void;
  style: CSS;
  showChatBanner: boolean;
  bannerHeight?: string;
  isChatActive: boolean;
  handleLaunch: () => void;
}

const styles = {
  clear: {
    color: '$text-danger',
    '&:hover': {
      bgColor: '$text-danger',
      color: 'white',
    },
  },
  footer: {
    borderTop: '1px solid $border-primary',
    padding: '$medium',
  },
};

function FilterPanel({
  doSearch,
  onClose,
  style,
  filters,
  showChatBanner,
  bannerHeight,
  isChatActive,
  handleLaunch,
}: IFilterPanel) {
  const { t } = useTranslation();
  const { clearFilters } = useContext(FilterContext);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const handleCancelDialog = () => {
    setIsDialogOpen(false);
  };

  const handleConfirmDialog = () => {
    clearFilters();
    doSearch();
    setIsDialogOpen(false);
  };

  return (
    <SidePanel
      title={t('core.filters')}
      onClose={onClose}
      style={style}
      bannerHeight={bannerHeight}
    >
      <Box css={{ flexGrow: 1, overflowY: 'scroll' }}>
        {showChatBanner && (
          <ChatBanner isActive={isChatActive} handleLaunch={handleLaunch} />
        )}
        <FilterPanelContent filters={filters} />
      </Box>
      <Flex align="center" justify="between" css={styles.footer}>
        <Button
          css={styles.clear}
          onPress={() => setIsDialogOpen(true)}
          size="small"
          variant="tertiary"
        >
          {t('global.clearAll')}
        </Button>
        <Button size="small" onPress={doSearch}>
          {t('core.applyFilters')}
        </Button>
      </Flex>
      <DialogModal
        title={t('core.dialogClearFiltersTitle')}
        description={t('core.dialogClearFiltersDescription')}
        confirmLabel={t('global.clearAll')}
        onCancel={handleCancelDialog}
        onConfirm={handleConfirmDialog}
        isOpen={isDialogOpen}
      />
    </SidePanel>
  );
}

export default FilterPanel;
